import { Link } from "react-router-dom";
import PageHero from "@/components/base/PageHero";
import ScrollReveal from "@/components/base/ScrollReveal";
import SectionHeading from "@/components/base/SectionHeading";
import { RECRUIT_CATEGORIES, RECRUIT_FAQS, withBase } from "@/constants/site";

const ANCHORS = [
  { id: "positions", label: "募集職種" },
  { id: "growth", label: "得られる経験" },
  { id: "career", label: "キャリア" },
  { id: "culture", label: "働く環境" },
  { id: "faq", label: "FAQ" },
];

const TOTAL_JOBS = RECRUIT_CATEGORIES.reduce((sum, c) => sum + c.jobs.length, 0);

const PERSONAS = [
  {
    no: "01",
    title: "自ら考え、動ける人",
    desc: "指示を待つのではなく、自ら課題を見つけ、挑戦できる方を歓迎します。",
  },
  {
    no: "02",
    title: "仲間と共に成長したい人",
    desc: "一人ではなく、チームで成果を追求し、共に成長できる方を求めています。",
  },
  {
    no: "03",
    title: "変化を楽しめる人",
    desc: "時代の変化を捉え、新しい価値づくりに前向きに取り組める方をお待ちしています。",
  },
];

const GROWTH = [
  {
    no: "01",
    title: "多角的な事業で、幅広い経験を",
    desc: "営業・マーケティング・人材育成・DX・飲食と、グループ内に多様な事業があります。一つの業界にとどまらない視野とスキルが身につきます。",
  },
  {
    no: "02",
    title: "若いうちから、任される",
    desc: "年次や経歴よりも、意欲と行動を見ています。手を挙げた人にはチャンスを。早い段階から現場の最前線や新しいプロジェクトに挑戦できます。",
  },
  {
    no: "03",
    title: "「人を育てる」ノウハウで、伸ばす",
    desc: "営業支援・人材育成を事業として行う私たちだからこそ、育成には本気です。先輩・上司が伴走し、成長のプロセスを一緒に設計します。",
  },
];

const CAREER_STEPS = [
  {
    step: "STEP 01",
    role: "メンバー",
    period: "入社〜1年目",
    level: 1,
    work: "現場でお客様への提案・販売を担当。先輩との同行やOJTを通じて、営業の基礎を身につけます。",
    skills: ["ヒアリング力", "提案力", "ビジネスマナー"],
  },
  {
    step: "STEP 02",
    role: "リーダー",
    period: "2〜3年目",
    level: 2,
    work: "数名のチームを率い、目標管理や後輩の育成を担います。自分の成果から、チームの成果へ。",
    skills: ["目標設計", "後輩育成", "チームビルディング"],
  },
  {
    step: "STEP 03",
    role: "マネージャー",
    period: "4〜5年目",
    level: 3,
    work: "拠点・事業部の運営や営業戦略の立案、採用活動に携わり、組織づくりを担います。",
    skills: ["戦略立案", "数値管理", "組織運営"],
  },
  {
    step: "STEP 04",
    role: "事業責任者",
    period: "6年目〜",
    level: 4,
    work: "新規事業の立ち上げやグループ会社の経営に参画し、事業そのものをつくる立場へ。",
    skills: ["事業開発", "経営視点", "意思決定"],
  },
];

const CAREER_BAR_COLORS = ["bg-accent-200", "bg-accent-300", "bg-accent-500", "bg-accent-700"];

const CULTURE = [
  {
    title: "仲間と、共に挑む",
    desc: "一人で抱え込まず、チームで成果を追いかける文化。成功も失敗も共有し、全員で次の一手を考えます。",
  },
  {
    title: "熱量で、応える",
    desc: "想いには、想いで応える。本気で挑戦する人を、周りが本気で応援する雰囲気があります。",
  },
  {
    title: "誠実さと、感謝を大切に",
    desc: "目先の利益より、長く続く信頼を。お客様にも仲間にも、誠実に向き合うことを大切にしています。",
  },
];

const CULTURE_PHOTOS = [
  {
    seq: "revra-culture-team",
    image: withBase("/images/recruit/culture-team.jpg"),
    alt: "チームでディスカッションする様子",
  },
  {
    seq: "revra-culture-celebrate",
    image: withBase("/images/recruit/culture-celebrate.jpg"),
    alt: "成果を喜び合う仲間",
  },
  {
    seq: "revra-culture-mentor",
    image: withBase("/images/recruit/culture-mentor.jpg"),
    alt: "先輩が後輩をサポートする様子",
  },
  {
    seq: "revra-culture-lunch",
    image: withBase("/images/recruit/culture-lunch.jpg"),
    alt: "休憩中に談笑する社員",
  },
];

export default function RecruitPage() {
  return (
    <div className="w-full bg-background-50">
      <PageHero en="RECRUIT" ja="採用情報" crumbs={[{ label: "採用情報" }]} />

      {/* Section anchors */}
      <nav
        aria-label="採用情報内ナビゲーション"
        className="sticky top-[69px] z-30 w-full border-b border-background-300/70 bg-background-50/95 backdrop-blur md:top-[77px]"
      >
        <ul className="mx-auto flex w-full max-w-7xl gap-6 overflow-x-auto px-6 md:gap-10 md:px-12 lg:justify-center lg:px-20">
          {ANCHORS.map((anchor) => (
            <li key={anchor.id} className="shrink-0">
              <a
                href={`#${anchor.id}`}
                className={`block whitespace-nowrap py-4 font-body text-[13px] tracking-[0.12em] transition-colors hover:text-accent-700 ${
                  anchor.id === "positions" ? "font-medium text-accent-700" : "text-foreground-700"
                }`}
              >
                {anchor.label}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      {/* Message */}
      <section className="w-full py-20 md:py-28">
        <div className="mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-12 px-6 md:px-12 lg:grid-cols-[1.2fr_1fr] lg:gap-20 lg:px-20">
          <ScrollReveal>
            <span className="font-label text-xs tracking-[0.4em] text-accent-700">MESSAGE</span>
            <h2 className="mt-6 font-display text-[28px] leading-[1.7] tracking-[0.04em] text-foreground-950 md:text-5xl md:leading-[1.6]">
              挑戦するヒトが、
              <br />
              次の時代をつくる。
            </h2>
            <span className="mt-8 block h-px w-20 bg-accent-500" />
            <p className="mt-8 max-w-xl font-body text-[15px] leading-[2.1] text-foreground-700">
              私たちは、一人ひとりの可能性が花開く社会を目指しています。
              そのために、自ら挑戦し、仲間と共に成長できる方をお待ちしています。
              経験やスキルよりも、「やってみたい」という想いを大切にしています。
            </p>
            <div className="mt-10 flex flex-col gap-4 sm:flex-row">
              <a
                href="#positions"
                className="inline-flex items-center justify-center gap-3 whitespace-nowrap bg-accent-600 px-9 py-4 font-body text-[13px] font-medium tracking-[0.2em] text-background-50 transition-colors hover:bg-foreground-950"
              >
                募集職種を見る
                <i className="ri-arrow-down-line" />
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-3 whitespace-nowrap border border-foreground-950 px-9 py-4 font-body text-[13px] tracking-[0.2em] text-foreground-950 transition-colors hover:bg-foreground-950 hover:text-background-50"
              >
                エントリー
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={150}>
            <div className="relative mx-auto w-full max-w-sm lg:mr-0">
              <div className="absolute -bottom-4 -right-4 h-full w-full border border-accent-500/60" />
              <div className="relative aspect-[4/5] w-full overflow-hidden bg-background-200">
                <img
                  src={withBase("/images/recruit/recruit-message.jpg")}
                  alt="REVRA Group 採用イメージ"
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Positions */}
      <section id="positions" className="w-full scroll-mt-32 bg-foreground-950 py-20 md:py-28">
        <div className="mx-auto w-full max-w-7xl px-6 md:px-12 lg:px-20">
          <ScrollReveal>
            <SectionHeading label="募集職種" en="Positions" dark />
            <p className="mt-8 max-w-2xl font-body text-[15px] leading-[2] text-background-300">
              現在、<span className="font-medium text-accent-300">{RECRUIT_CATEGORIES.length}カテゴリ・{TOTAL_JOBS}職種</span>
              で仲間を募集しています。未経験からのチャレンジも歓迎します。
            </p>
          </ScrollReveal>

          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8">
            {RECRUIT_CATEGORIES.map((category, index) => (
              <ScrollReveal key={category.slug} delay={index * 100}>
                <article className="group flex h-full flex-col border border-background-50/15 bg-background-50/[0.03] p-7 transition-colors duration-300 hover:border-accent-500/70 md:p-10">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <span className="font-label text-xs tracking-[0.28em] text-accent-400">
                        {category.en.toUpperCase()}
                      </span>
                      <h3 className="mt-3 font-heading text-2xl font-medium tracking-[0.06em] text-background-50 md:text-3xl">
                        {category.title}
                      </h3>
                    </div>
                    <span className="text-outline font-label text-5xl leading-none md:text-6xl">
                      0{index + 1}
                    </span>
                  </div>
                  <p className="mt-5 font-body text-sm leading-[2] text-background-300">
                    {category.desc}
                  </p>

                  <ul className="mt-6 flex flex-1 flex-col border-t border-background-50/15">
                    {category.jobs.map((job) => (
                      <li key={job.slug}>
                        <Link
                          to={`/recruit/${category.slug}/${job.slug}`}
                          className="flex items-center justify-between gap-4 border-b border-background-50/15 py-4 font-body text-[15px] text-background-100 transition-colors hover:text-accent-300"
                        >
                          {job.title}
                          <i className="ri-arrow-right-line text-accent-400" />
                        </Link>
                      </li>
                    ))}
                  </ul>

                  <Link
                    to={`/recruit/${category.slug}`}
                    className="mt-8 inline-flex w-full items-center justify-center gap-3 whitespace-nowrap bg-background-50 px-8 py-3.5 font-body text-[13px] tracking-[0.2em] text-foreground-950 transition-colors hover:bg-accent-500 sm:w-fit"
                  >
                    {category.title}の募集を見る
                    <i className="ri-arrow-right-line" />
                  </Link>
                </article>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Growth */}
      <section id="growth" className="w-full scroll-mt-32 py-20 md:py-28">
        <div className="mx-auto w-full max-w-7xl px-6 md:px-12 lg:px-20">
          <ScrollReveal>
            <SectionHeading label="REVRA Groupで得られる経験" en="Growth" />
            <p className="mt-8 max-w-2xl font-body text-[15px] leading-[2] text-foreground-700">
              「ヒトの可能性を、価値に変える」。その理念は、お客様だけでなく、
              ここで働く仲間一人ひとりにも向けられています。
            </p>
          </ScrollReveal>

          <div className="mt-14 flex flex-col border-t border-background-300/70">
            {GROWTH.map((item, index) => (
              <ScrollReveal key={item.no} delay={index * 80}>
                <div className="grid grid-cols-1 gap-4 border-b border-background-300/70 py-10 md:grid-cols-[160px_1fr_1.3fr] md:items-center md:gap-10 md:py-12">
                  <span className="text-outline font-label text-6xl leading-none md:text-7xl">
                    {item.no}
                  </span>
                  <h3 className="font-heading text-xl font-medium leading-[1.7] tracking-[0.04em] text-foreground-950 md:text-2xl">
                    {item.title}
                  </h3>
                  <p className="font-body text-sm leading-[2] text-foreground-700">{item.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Career */}
      <section id="career" className="w-full scroll-mt-32 bg-background-100 py-20 md:py-28">
        <div className="mx-auto w-full max-w-7xl px-6 md:px-12 lg:px-20">
          <ScrollReveal>
            <SectionHeading label="挑戦できる仕事とキャリア" en="Career" />
            <p className="mt-8 max-w-2xl font-body text-[15px] leading-[2] text-foreground-700">
              グループ内の多様な事業が、あなたのキャリアの選択肢になります。
              現場での経験を土台に、チーム、組織、そして事業へ。任される範囲が段階的に広がっていきます。
            </p>
          </ScrollReveal>

          {/* Step-up chart */}
          <ScrollReveal delay={100}>
            <figure className="mt-14">
              <figcaption className="mb-4 flex items-center justify-between font-body text-xs text-foreground-600">
                <span className="flex items-center gap-2">
                  <i className="ri-arrow-up-line text-accent-700" />
                  役割・裁量の大きさ
                </span>
                <span>経験年数（目安）</span>
              </figcaption>
              <div className="relative h-[260px] border-b border-l border-foreground-400/60 md:h-[380px]">
                {[25, 50, 75].map((y) => (
                  <span
                    key={y}
                    aria-hidden
                    className="absolute inset-x-0 border-t border-dashed border-background-400/70"
                    style={{ bottom: `${y}%` }}
                  />
                ))}
                <ol className="absolute inset-0 grid grid-cols-4 items-end gap-[2px] px-[2px] md:gap-3 md:px-3">
                  {CAREER_STEPS.map((item, index) => (
                    <li
                      key={item.step}
                      className="group relative flex h-full flex-col justify-end"
                      title={`${item.step} ${item.role}（${item.period}）`}
                    >
                      <div
                        className={`career-bar relative flex flex-col items-center justify-start rounded-t-[4px] pt-3 transition-opacity group-hover:opacity-90 md:pt-5 ${CAREER_BAR_COLORS[index]}`}
                        style={{ height: `${item.level * 25}%`, animationDelay: `${index * 150}ms` }}
                      >
                        <span
                          className={`font-label text-[10px] tracking-[0.2em] md:text-xs ${
                            index >= 2 ? "text-background-50/90" : "text-foreground-800"
                          }`}
                        >
                          {item.step}
                        </span>
                        <span
                          className={`mt-1 px-1 text-center font-heading text-[13px] font-medium leading-tight tracking-[0.02em] md:mt-2 md:text-xl ${
                            index >= 2 ? "text-background-50" : "text-foreground-950"
                          }`}
                        >
                          {item.role}
                        </span>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
              <div className="grid grid-cols-4 gap-[2px] px-[2px] pt-3 md:gap-3 md:px-3">
                {CAREER_STEPS.map((item) => (
                  <span
                    key={item.step}
                    className="text-center font-body text-[11px] text-foreground-700 md:text-sm"
                  >
                    {item.period}
                  </span>
                ))}
              </div>
            </figure>
          </ScrollReveal>

          {/* Step details */}
          <ol className="mt-16 flex flex-col border-t border-foreground-950/80">
            {CAREER_STEPS.map((item, index) => (
              <ScrollReveal key={item.step} delay={index * 80}>
                <li className="grid grid-cols-1 gap-4 border-b border-background-300 py-8 md:grid-cols-[200px_1fr_220px] md:gap-10 md:py-10">
                  <div className="flex items-baseline gap-4 md:flex-col md:gap-2">
                    <span className="font-label text-sm tracking-[0.3em] text-accent-700">
                      {item.step}
                    </span>
                    <span className="font-heading text-xl font-medium tracking-[0.04em] text-foreground-950 md:text-2xl">
                      {item.role}
                    </span>
                    <span className="font-body text-xs text-foreground-600">{item.period}</span>
                  </div>
                  <p className="font-body text-sm leading-[2] text-foreground-700">{item.work}</p>
                  <div>
                    <span className="font-label text-[11px] tracking-[0.26em] text-secondary-500">
                      SKILLS
                    </span>
                    <p className="mt-2 font-body text-sm leading-[1.9] text-foreground-950">
                      {item.skills.join("・")}
                    </p>
                  </div>
                </li>
              </ScrollReveal>
            ))}
          </ol>
          <p className="mt-6 font-body text-xs leading-[1.9] text-foreground-600">
            ※ 期間はあくまで目安です。成果や意欲に応じて、より早いステップアップも可能です。
          </p>
        </div>
      </section>

      {/* Culture */}
      <section id="culture" className="w-full scroll-mt-32 bg-foreground-950 py-20 md:py-28">
        <div className="mx-auto w-full max-w-7xl px-6 md:px-12 lg:px-20">
          <ScrollReveal>
            <SectionHeading label="働く環境・仲間・雰囲気" en="Culture" dark />
            <p className="mt-8 max-w-2xl font-body text-[15px] leading-[2] text-background-300">
              真剣に挑み、成果を喜び合い、困ったときは支え合う。
              年次や立場を越えて、仲間と本音で向き合える職場です。
            </p>
          </ScrollReveal>

          <ScrollReveal delay={100}>
            <div className="mt-12 grid grid-cols-2 gap-2 md:grid-cols-4 md:grid-rows-2 md:gap-3">
              {CULTURE_PHOTOS.map((photo, index) => (
                <div
                  key={photo.seq}
                  className={`overflow-hidden bg-foreground-900 ${
                    index === 0
                      ? "col-span-2 aspect-[4/3] md:row-span-2 md:aspect-auto"
                      : index === 3
                        ? "col-span-2 aspect-[16/9] md:aspect-auto"
                        : "aspect-square"
                  }`}
                >
                  <img
                    src={photo.image}
                    alt={photo.alt}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                  />
                </div>
              ))}
            </div>
          </ScrollReveal>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3">
            {CULTURE.map((item, index) => (
              <ScrollReveal key={item.title} delay={index * 100}>
                <div
                  className={`py-8 md:px-8 md:py-2 ${
                    index > 0 ? "border-t border-background-50/15 md:border-l md:border-t-0" : ""
                  } ${index === 0 ? "md:pl-0" : ""}`}
                >
                  <span className="font-label text-sm tracking-[0.3em] text-accent-400">
                    0{index + 1}
                  </span>
                  <h3 className="mt-3 font-heading text-xl tracking-[0.04em] text-background-50 md:text-2xl">
                    {item.title}
                  </h3>
                  <p className="mt-4 font-body text-sm leading-[2] text-background-300">
                    {item.desc}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Persona */}
      <section className="w-full py-20 md:py-28">
        <div className="mx-auto w-full max-w-7xl px-6 md:px-12 lg:px-20">
          <ScrollReveal>
            <SectionHeading label="求める人物像" en="Person" />
          </ScrollReveal>
          <div className="mt-12 flex flex-col border-t border-foreground-950/80">
            {PERSONAS.map((persona, index) => (
              <ScrollReveal key={persona.no} delay={index * 100}>
                <div className="grid grid-cols-1 gap-3 border-b border-background-300 py-8 md:grid-cols-[80px_1fr_1.2fr] md:items-baseline md:gap-10 md:py-10">
                  <span className="font-label text-2xl tracking-[0.1em] text-accent-600">
                    {persona.no}
                  </span>
                  <h3 className="font-display text-2xl tracking-[0.04em] text-foreground-950 md:text-3xl">
                    {persona.title}
                  </h3>
                  <p className="font-body text-sm leading-[2] text-foreground-700">{persona.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="w-full scroll-mt-32 bg-background-100 py-20 md:py-28">
        <div className="mx-auto w-full max-w-7xl px-6 md:px-12 lg:px-20">
          <ScrollReveal>
            <SectionHeading label="よくある質問" en="FAQ" />
          </ScrollReveal>

          <div className="mt-12 flex flex-col gap-3">
            {RECRUIT_FAQS.map((faq, index) => (
              <ScrollReveal key={faq.q} delay={index * 80}>
                <details className="group border border-background-300/70 bg-background-50 px-6 py-5 md:px-8 md:py-6">
                  <summary className="flex cursor-pointer list-none items-start justify-between gap-6">
                    <span className="flex items-start gap-4">
                      <span className="font-label text-lg leading-none tracking-[0.1em] text-accent-600">Q</span>
                      <span className="font-body text-[15px] font-medium tracking-[0.02em] text-foreground-950">
                        {faq.q}
                      </span>
                    </span>
                    <i className="ri-add-line mt-0.5 shrink-0 text-lg text-foreground-500 transition-transform duration-300 group-open:rotate-45" />
                  </summary>
                  <p className="mt-4 flex items-start gap-4 border-t border-background-300/70 pt-4 font-body text-sm leading-[2] text-foreground-700">
                    <span className="font-label text-lg leading-none tracking-[0.1em] text-secondary-500">A</span>
                    <span>{faq.a}</span>
                  </p>
                </details>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Entry */}
      <section className="relative w-full overflow-hidden bg-foreground-950 py-24 md:py-32">
        <div className="absolute inset-0">
          <img
            src={withBase("/images/recruit/recruit-entry.jpg")}
            alt=""
            className="h-full w-full object-cover opacity-50"
          />
        </div>
        <div className="absolute inset-0 bg-foreground-950/60" />
        <div className="relative z-10 mx-auto w-full max-w-7xl px-6 md:px-12 lg:px-20">
          <ScrollReveal>
            <div className="flex flex-col items-center text-center">
              <span className="font-label text-sm tracking-[0.5em] text-accent-300">ENTRY</span>
              <p className="mt-6 font-display text-[28px] leading-[1.7] tracking-[0.04em] text-background-50 md:text-5xl">
                次の時代を、
                <br className="md:hidden" />
                一緒につくろう。
              </p>
              <p className="mt-6 max-w-xl font-body text-[15px] leading-[2] text-background-200">
                少しでも興味を持っていただけたら、まずはお気軽にご連絡ください。
                カジュアルなご相談も歓迎しています。
              </p>
              <div className="mt-10 flex w-full flex-col items-center gap-4 sm:w-auto sm:flex-row">
                <Link
                  to="/contact"
                  className="inline-flex w-full items-center justify-center gap-3 whitespace-nowrap bg-accent-600 px-12 py-4 font-body text-[13px] font-medium tracking-[0.24em] text-background-50 transition-colors hover:bg-accent-500 sm:w-auto"
                >
                  応募・お問い合わせ
                  <i className="ri-arrow-right-line" />
                </Link>
                <a
                  href="#positions"
                  className="inline-flex w-full items-center justify-center gap-3 whitespace-nowrap border border-background-100/50 px-12 py-4 font-body text-[13px] tracking-[0.24em] text-background-50 transition-colors hover:bg-background-50 hover:text-foreground-950 sm:w-auto"
                >
                  募集職種を見る
                  <i className="ri-arrow-up-line" />
                </a>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
